import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Bot, Upload, Users, Play, HardDrive } from "lucide-react";
import { PlanLimits, UsageStats, isUnlimited } from "@/lib/plan-limits";
import { cn } from "@/lib/utils";

interface UsageCardProps {
  usage: UsageStats | null;
  limits: PlanLimits;
  className?: string;
}

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  used: number;
  max: number;
  unit?: string;
}

function UsageItem({ icon, label, used, max, unit = "" }: UsageItemProps) {
  const unlimited = isUnlimited(max);
  const percentage = unlimited ? 0 : Math.min((used / max) * 100, 100);
  const isNearLimit = !unlimited && percentage >= 80;
  const isAtLimit = !unlimited && percentage >= 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <span className={cn(
          "font-medium tabular-nums",
          isAtLimit && "text-destructive",
          isNearLimit && !isAtLimit && "text-warning"
        )}>
          {used}{unit}
          {!unlimited && <span className="text-muted-foreground">/{max}{unit}</span>}
          {unlimited && <span className="text-muted-foreground"> (unlimited)</span>}
        </span>
      </div>
      {!unlimited && (
        <Progress 
          value={percentage} 
          className={cn(
            "h-1.5",
            isAtLimit && "[&>div]:bg-destructive",
            isNearLimit && !isAtLimit && "[&>div]:bg-warning"
          )}
        />
      )}
    </div>
  );
}

export function UsageCard({ usage, limits, className }: UsageCardProps) {
  if (!usage) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
            Loading usage data...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Usage This Period</CardTitle>
        <Badge variant="outline" className="text-xs font-normal">
          Resets monthly
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <UsageItem
          icon={<Bot className="h-4 w-4" />}
          label="AI calls today"
          used={usage.aiCallsToday}
          max={limits.rateLimits.aiCallsPerDay}
        />
        
        <UsageItem
          icon={<Upload className="h-4 w-4" />}
          label="Uploads today"
          used={usage.uploadsToday}
          max={limits.rateLimits.uploadsPerDay}
        />
        
        <UsageItem
          icon={<Play className="h-4 w-4" />}
          label="Runs this month"
          used={usage.runsThisMonth}
          max={limits.maxRunsPerMonth}
        />
        
        <UsageItem
          icon={<Users className="h-4 w-4" />}
          label="Team seats"
          used={usage.seatsUsed}
          max={limits.maxSeats}
        />
        
        <UsageItem
          icon={<HardDrive className="h-4 w-4" />}
          label="Storage"
          used={usage.storageUsedGB}
          max={limits.maxStorageGB}
          unit="GB"
        />
      </CardContent>
    </Card>
  );
}
